<script>
  import { getContext, hasContext, tick } from "svelte";
  import { writable } from "svelte/store";
  import Header from "lib/components/Header.svelte";
  import SurveyEstimatedTime from "./components/SurveyEstimatedTime.svelte";
  import SurveyRandomAnswerButton from "./components/SurveyRandomAnswerButton.svelte";
  import SurveyStepIntro from "./components/SurveyStepIntro.svelte";
  import { NumberInput } from "carbon-components-svelte";
  import Modal from "../../../lib/components/Modal.svelte";
  import { onMount } from "svelte/internal";
  import { ROUTES } from "@utils/routes.js";
  import { TextInput, TextArea, Button } from "paxray-components-svelte";

  const role = hasContext("role") ? getContext("role") : writable(null);

  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";
  import {
    translate,
    translateOption,
    translateQuestion,
    translateStep,
  } from "./surveyTranslations.js";
  import {
    ARROW_LEFT_ICON,
    ARROW_RIGHT_ICON,
    CHECK_DARK_ICON,
    CHECKBOX_ICON,
    CHECKBOX_SELECTED_WHITE_ICON,
    CLOSE_ICON,
    EDIT_ICON,
    INFO_ICON,
    PLUS_ICON,
  } from "@utils/icons.js";

  let user = localStorage.getItem("username") || "User";
  let openModalHelp;
  let helpText = "";
  let step = 0;
  let totalStep = 0;
  let totalSteps = 0;
  let options = [];
  let timeLeft = "";
  let allowCustomTask = true;
  let openModalAddTask = false;
  let newTask = "";
  let currentDimensionState = 0;
  let currentText;
  let currentNote;
  let disabledNext = false;
  let addDetailPressed = false;
  let openLoadModal = false;
  let openFocusTaskModal = false;
  let openNoProposalModal = false;
  let focusTaskSearch = "";
  let focusTaskPage = 0;
  let departments;
  let areas;
  let tasks;
  let businessUnits;
  let problemTask = [];
  //let openModalSendAnswers = false;
  const defaultDetails = {
    _proposal_enabled: false,
    problem_short_name: "",
    problem_desc: "",
    solution_idea: "",
    avg_time: "",
    number_of_colleagues: "",
    occurrences_per_year: "",
    time_lost_minutes: "",
    task_occurrences_per_month: "",
    task_duration_minutes: "",
    task_duration_seconds: "",
    standard_case_share_percent: "",
    related_topics: [],
    impact_on_task_time: "",
    occurrence_frequency: "",
  };
  const improvementTopics = [
    "Unnecessary effort",
    "Errors / rework",
    "System waiting time",
    "Waiting for colleagues",
    "Manual workaround",
  ];
  const impactOptions = ["<10%", "10-25%", "25-50%", ">50%"];
  const frequencyOptions = ["Rarely", "Sometimes", "Often", "Almost always"];
  const impactFactors = {
    "<10%": 0.05,
    "10-25%": 0.175,
    "25-50%": 0.375,
    ">50%": 0.65,
  };
  const frequencyFactors = {
    Rarely: 0.1,
    Sometimes: 0.35,
    Often: 0.7,
    "Almost always": 1,
  };

  function updateDerivedDetail(detail) {
    detail.occurrences_per_year = Math.round(
      Number(detail.task_occurrences_per_month || 0) *
        12 *
        Number(frequencyFactors[detail.occurrence_frequency] || 0),
    );
    detail.time_lost_minutes = Math.round(
      (Number(
        detail.task_duration_seconds ||
          Number(detail.task_duration_minutes || 0) * 60,
      ) /
        60) *
        Number(impactFactors[detail.impact_on_task_time] || 0),
    );
  }

  function formatDuration(seconds) {
    const value = Math.max(0, Number(seconds || 0));
    return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
  }

  function setTaskDurationPart(detail, part, input) {
    const currentSeconds = Math.max(
      0,
      Number(detail.task_duration_seconds || 0),
    );
    const value = input.value === "" ? 0 : Number(input.value);
    const minutes =
      part === "minutes"
        ? Math.max(0, Math.trunc(Number(value || 0)))
        : Math.floor(currentSeconds / 60);
    const seconds =
      part === "seconds"
        ? Math.min(60, Math.max(0, Math.trunc(Number(value || 0))))
        : currentSeconds % 60;
    input.value = String(part === "minutes" ? minutes : seconds);
    detail.task_duration_seconds = minutes * 60 + seconds || null;
    detail.task_duration_minutes = detail.task_duration_seconds
      ? Math.ceil(detail.task_duration_seconds / 60)
      : null;
    syncTaskBaseline(getCurrentProblemTask(), detail);
  }

  function clampBaselineNumber(
    task,
    detail,
    field,
    minimum,
    maximum = Infinity,
  ) {
    if (detail[field] === "" || detail[field] == null)
      return syncTaskBaseline(task, detail);
    detail[field] = Math.min(
      maximum,
      Math.max(minimum, Math.trunc(Number(detail[field]) || 0)),
    );
    syncTaskBaseline(task, detail);
  }

  function syncTaskBaseline(task, source) {
    task.details.forEach((detail) => {
      detail.task_occurrences_per_month = source.task_occurrences_per_month;
      detail.task_duration_minutes = source.task_duration_minutes;
      detail.task_duration_seconds = source.task_duration_seconds;
      detail.standard_case_share_percent = source.standard_case_share_percent;
      detail.number_of_colleagues = source.number_of_colleagues;
      updateDerivedDetail(detail);
    });
    selectedTasks = [...selectedTasks];
    setDisabledNext(totalStep);
  }

  function toggleImprovementTopic(detail, topic) {
    detail.related_topics = detail.related_topics || [];
    detail.related_topics = detail.related_topics.includes(topic)
      ? detail.related_topics.filter((item) => item !== topic)
      : [...detail.related_topics, topic];
    selectedTasks = [...selectedTasks];
    setDisabledNext(totalStep);
  }

  function isTaskBaselineComplete(detail) {
    return Boolean(
      detail.task_occurrences_per_month > 0 &&
        detail.task_duration_seconds > 0 &&
        detail.standard_case_share_percent !== "" &&
        detail.standard_case_share_percent != null &&
        detail.standard_case_share_percent >= 0 &&
        detail.standard_case_share_percent <= 100 &&
        (detail.number_of_colleagues || detail.number_of_colleagues === 0),
    );
  }

  function isProposalEnabled(detail) {
    return (
      detail?._proposal_enabled ??
      Boolean(
        detail?.problem_desc?.trim() ||
          detail?.related_topics?.length ||
          detail?.impact_on_task_time ||
          detail?.occurrence_frequency,
      )
    );
  }

  function isProposalComplete(detail) {
    return Boolean(
      detail.problem_desc?.trim() &&
        detail.related_topics?.length &&
        detail.impact_on_task_time &&
        detail.occurrence_frequency,
    );
  }

  function isTaskDetailsComplete(task) {
    const baseline = task?.details?.[0];
    return Boolean(
      baseline &&
        isTaskBaselineComplete(baseline) &&
        task.details.filter(isProposalEnabled).every(isProposalComplete),
    );
  }

  function addImprovementDetail(task) {
    const baseline = task.details[0] || defaultDetails;
    if (!task.details.some(isProposalEnabled)) {
      baseline._proposal_enabled = true;
      task.details = [...task.details];
      selectedTasks = [...selectedTasks];
      setDisabledNext(totalStep);
      return;
    }
    task.details = [
      ...task.details,
      {
        ...defaultDetails,
        _proposal_enabled: true,
        related_topics: [],
        task_occurrences_per_month: baseline.task_occurrences_per_month,
        task_duration_minutes: baseline.task_duration_minutes,
        task_duration_seconds: baseline.task_duration_seconds,
        standard_case_share_percent: baseline.standard_case_share_percent,
        number_of_colleagues: baseline.number_of_colleagues,
      },
    ];
    selectedTasks = [...selectedTasks];
    setDisabledNext(totalStep);
  }

  function getLastProposal(task) {
    const proposals = task?.details?.filter(isProposalEnabled) || [];
    return proposals[proposals.length - 1];
  }

  function getCurrentProblemTask() {
    const index =
      totalStep - (step + selectedAreas.length + dimensions.length) + 2;
    const current = problemTask[index];
    return selectedTasks.find((task) => task.task.id === current?.id);
  }

  function getCurrentProblemTaskArea() {
    const taskId = getCurrentProblemTask()?.task?.id;
    return (
      areas?.find((area) => area.tasks?.some((task) => task.id === taskId))
        ?.name || t("selectedArea")
    );
  }

  function removeImprovementDetail(task, index) {
    if (!task) return;
    if (index === 0) {
      Object.assign(task.details[0], {
        problem_short_name: "",
        problem_desc: "",
        solution_idea: "",
        related_topics: [],
        impact_on_task_time: "",
        occurrence_frequency: "",
        _proposal_enabled: false,
      });
    } else {
      task.details = task.details.filter(
        (_, detailIndex) => detailIndex !== index,
      );
    }
    selectedTasks = [...selectedTasks];
    setDisabledNext(totalStep);
  }
  let userAnswersId;
  let allowAnswer = false;
  let status;

  let notAvailableMessage = "Survey not available";

  let answers = [];
  let openModalSaveAnswers = false;

  const saved = localStorage.getItem("surveyProgress");
  let idSurvey;

  let urlParams = new URLSearchParams(window.location.search);
  let idSurveyUser = urlParams.get("survey_user");
  const previewSurveyId = urlParams.get("preview");
  const previewMode = Boolean(previewSurveyId);
  if (previewMode) idSurvey = previewSurveyId;
  let sentAnswerd = false;
  let reviewEditMode = false;

  let dataSurvey;
  let surveyLanguage = "en";
  let surveyUserId;
  let loading = true;
  let dimensions = [];
  let timeDetailStart = 0;
  let timeDetailTotal = 0;
  let timeStart = 0;
  let timeSaved = 0;
  let autoSaveTimer;
  let lastAutoSaveSignature = "";
  let stepIntroTitle = "";
  let stepIntroDescription = "";
  let stepIntroVariant = "default";
  $: isAdmin = $role?.role === "admin";

  $: if (sentAnswerd && !previewMode) updateSurveyUsers();
  $: surveyLanguage = dataSurvey?.language || "en";
  $: setOptions(step, totalStep);
  $: setTimeLeft(totalStep);
  $: if (step === 3 && totalStep) setCurrentDimensionState();
  $: getStepText(
    step,
    totalStep,
    surveyLanguage,
    selectedAreas,
    selectedTasks,
    dimensions,
  );
  $: stepIntroTitle = getStepIntroTitle(
    step,
    totalStep,
    surveyLanguage,
    selectedAreas,
    dimensions,
    problemTask,
  );
  $: stepIntroDescription = getStepIntroDescription(step, currentText);
  $: stepIntroVariant = getStepIntroVariant(step);
  $: setDisabledNext(totalStep);
  $: if (businessUnits && tasks && areas && departments && dataSurvey)
    loading = false;
  $: if (!loading) setSurveyOptions();
  $: if (step === 4) setProblemTask(selectedTasks);
  $: setTotalSteps(problemTask, steps, dimensions, areas);
  $: if (step === 5 && totalStep) {
    selectedTasks;
    resizeTaskTextareas();
  }
  $: if (step > 0 && allowAnswer && !sentAnswerd && !previewMode) {
    scheduleAutoSave(step, totalStep, selectedAreas, selectedTasks);
  }

  $: setStatus(status);

  function t(key) {
    return translate(surveyLanguage, key);
  }

  function resizeTaskTextareas() {
    tick().then(() => {
      const textAreas = document.querySelectorAll(
        ".task_detail_wrapper textarea",
      );
      textAreas.forEach((textarea) => {
        textarea.style.height = "auto";
        textarea.style.height = textarea.scrollHeight + "px";
      });
    });
  }

  function setStatus(status) {
    if (previewMode && dataSurvey) allowAnswer = true;
    else if (status === "RUNNING") allowAnswer = true;
    else if (status === "PENDING")
      notAvailableMessage = `${t("availableFrom")} </br>${dataSurvey.start_date}`;
    else if (status === "FINISHED")
      notAvailableMessage = `${t("closedSince")} </br>${dataSurvey.end_date}`;
  }

  function editReviewSection(section) {
    const reviewSteps = {
      areas: [1, 1],
      tasks: [2, 2],
      ratings: [3, selectedAreas.length + 2],
      details: [5, selectedAreas.length + dimensions.length + 3],
    };
    reviewEditMode = true;
    [step, totalStep] = reviewSteps[section];
  }

  function returnToReview() {
    reviewEditMode = false;
    step = 6;
    totalStep = Math.max(0, totalSteps - 1);
  }

  function isSurveyComplete() {
    const allAreasHaveTasks = selectedAreas.every((area) =>
      area.tasks.some((task) =>
        selectedTasks.some((selectedTask) => selectedTask.task.id === task.id),
      ),
    );
    const allRatingsComplete = selectedTasks.every((task) =>
      dimensions.every((dimension) => task.dimensions[dimension.id] != null),
    );
    const allDetailsComplete = selectedTasks
      .filter((task) => task.problem)
      .every((task) => isTaskDetailsComplete(task));
    return (
      selectedAreas.length > 0 &&
      selectedTasks.length > 0 &&
      allAreasHaveTasks &&
      allRatingsComplete &&
      allDetailsComplete
    );
  }

  function continueReviewEdit() {
    const incompleteAreaIndex = selectedAreas.findIndex(
      (area) =>
        !area.tasks.some((task) =>
          selectedTasks.some(
            (selectedTask) => selectedTask.task.id === task.id,
          ),
        ),
    );
    if (incompleteAreaIndex >= 0) {
      step = 2;
      totalStep = 2 + incompleteAreaIndex;
      return;
    }
    if (
      selectedTasks.some((task) =>
        dimensions.some((dimension) => task.dimensions[dimension.id] == null),
      )
    ) {
      step = 3;
      totalStep = selectedAreas.length + 2;
      return;
    }
    const incompleteDetailIndex = selectedTasks
      .filter((task) => task.problem)
      .findIndex((task) => !isTaskDetailsComplete(task));
    if (incompleteDetailIndex >= 0) {
      step = 5;
      totalStep =
        selectedAreas.length + dimensions.length + 3 + incompleteDetailIndex;
      return;
    }
    returnToReview();
  }

  function isNextDisabled() {
    if (step === 1) return selectedAreas.length === 0;
    if (step === 2) {
      const currentArea = selectedAreas[totalStep - step];
      return !currentArea?.tasks.some((task) =>
        selectedTasks.some((selectedTask) => selectedTask.task.id === task.id),
      );
    }
    if (step === 3) {
      const dimensionIndex = totalStep - (step + selectedAreas.length) + 1;
      const questionId = dimensions[dimensionIndex]?.id;
      return selectedTasks.some((task) => task.dimensions[questionId] == null);
    }
    if (step === 5) {
      const detailIndex =
        totalStep - (step + selectedAreas.length + dimensions.length) + 2;
      const currentTask = selectedTasks.find(
        (task) => task.task.id === problemTask[detailIndex]?.id,
      );
      return !isTaskDetailsComplete(currentTask);
    }
    if (step === 6) return true;
    return false;
  }

  function getRandomItem(items) {
    if (!items?.length) return null;
    return items[Math.floor(Math.random() * items.length)];
  }

  function createSelectedTask(task) {
    return {
      task,
      problem: false,
      dimensions: {},
      details: [{ ...defaultDetails, related_topics: [] }],
    };
  }

  function answerRandomAndContinue() {
    if (step === 0) {
      timeStart = Date.now();
      totalStep++;
      step++;
      return;
    }

    if (step === 1) {
      const area = getRandomItem(
        options.filter((option) => option.tasks?.length),
      );
      if (!area) return;
      selectedAreas = [area];
      selectedTasks = selectedTasks.filter((selectedTask) =>
        area.tasks.some((task) => task.id === selectedTask.task.id),
      );
      setDisabledNext(totalStep);
      advanceSurvey();
      return;
    }

    if (step === 2) {
      const task = getRandomItem(options);
      if (!task) return;
      if (
        !selectedTasks.some((selectedTask) => selectedTask.task.id === task.id)
      ) {
        selectedTasks = [...selectedTasks, createSelectedTask(task)];
      }
      setDisabledNext(totalStep);
      advanceSurvey();
      return;
    }

    if (step === 3) {
      const dimensionIndex = totalStep - (step + selectedAreas.length) + 1;
      const dimension = dimensions[dimensionIndex];
      const answer = getRandomItem(dimension?.answers);
      if (!dimension || !answer) return;
      selectedTasks.forEach((selectedTask) => {
        selectedTask.dimensions[dimension.id] = answer.value;
      });
      selectedTasks = [...selectedTasks];
      setCurrentDimensionState();
      setDisabledNext(totalStep);
      advanceSurvey();
      return;
    }

    if (step === 4) {
      const selectedIndex = Math.max(
        0,
        selectedTasks.findIndex((task) => task.problem),
      );
      selectedTasks = selectedTasks.map((selectedTask, index) => ({
        ...selectedTask,
        problem: index === selectedIndex,
        details: selectedTask.details || [
          { ...defaultDetails, related_topics: [] },
        ],
      }));
      setProblemTask(selectedTasks);
      advanceSurvey();
      return;
    }

    if (step === 5) {
      const currentTask = getCurrentProblemTask();
      if (!currentTask) return;
      currentTask.details = currentTask.details?.length
        ? currentTask.details
        : [{ ...defaultDetails, related_topics: [] }];
      Object.assign(currentTask.details[0], {
        task_occurrences_per_month: 1,
        task_duration_minutes: 1,
        task_duration_seconds: 60,
        number_of_colleagues: 0,
        standard_case_share_percent: 0,
        _proposal_enabled: false,
      });
      syncTaskBaseline(currentTask, currentTask.details[0]);
      setDisabledNext(totalStep);
      advanceSurvey();
    }
  }

  function getAnswerLabel(questionId, value) {
    const label =
      dimensions
        .find((dimension) => dimension.id === questionId)
        ?.answers.find((answer) => answer.value === value)?.answer || value;
    return translateOption(surveyLanguage, label);
  }

  function createNewTask() {
    if (previewMode) return;
    let body = { name: newTask, area: selectedAreas[totalStep - step].id };

    fetchWithSession(`/api/knextractor/tasks`, {
      method: "POST",
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          error = true;
          errorMsg = data.error;
          openModalAddTask = false;
          return;
        }
        //saveSuccessful = true;
        //successMsg = data.message + " successfully" || "Create successful";
        openModalAddTask = false;
        // Refresh the data after creation
        //refreshTables();
        getTasks();
        setOptions(step, totalStep);
      });
  }

  function setProblemTask(selectedTasks) {
    problemTask = [];
    problemTask = selectedTasks
      .filter((task) => task.problem)
      .map((task) => task.task);
    selectedTasks.forEach((task, index) => {
      if (task.problem && !task.details) {
        selectedTasks[index] = {
          dimensions: task.dimensions,
          task: task.task,
          problem: task.problem,
          details: [
            {
              problem_short_name: "",
              problem_desc: null,
              solution_idea: null,
              avg_time: null,
              number_of_colleagues: null,
              occurrences_per_year: null,
              time_lost_minutes: null,
              task_occurrences_per_month: null,
              task_duration_minutes: null,
              task_duration_seconds: null,
              standard_case_share_percent: null,
              related_topics: [],
              impact_on_task_time: "",
              occurrence_frequency: "",
              _proposal_enabled: false,
            },
          ],
        };
      }
    });
    setDisabledNext(totalStep);
  }
  let selectedAreas = [];
  let selectedTasks = [];

  let dimensionsPossibilities;

  let steps = [
    {
      heading: "Help us improve how work gets done",
      text: "Your feedback helps us identify process inefficiencies and uncover opportunities to improve workflows, collaboration, and overall efficiency.",
      note: "All responses are confidential and aggregated.",
      helpText: "Help text",
    },
    {
      heading: "General Overview",
      text: "Please first choose the areas you are working in and on which you want to give anonymized information.",
      note: "Please leave areas in which you do not want to contribute information unselected.",
    },
    {
      heading: `Overview`,
      text: `Please choose the main tasks in $department  you are working on in your daily doing and about which you want to give anonymized information.`,
      note: "Please leave tasks in which you do not want to contribute information unselected.",
    },
    {
      heading: "{Dimension}",
      text: "How often do you need to work on these task?",
      note: "To what extent do your task consist of organizing, storing or archiving documents that differ only minimally in structure or content?",
    },
    {
      heading: `Focus area`,
      text: `Help us understand what matters most. || You can provide more detailed information for selected tasks.`,
      note: "Based on your ratings, these tasks may offer relevant improvement potential. || Add details to the tasks where your experience can help most.",
    },
    {
      heading: "",
      text: "What bothers you about the respective activity and/or where do you see potential for improvement?",
      note: "Please describe ONE! problem or optimization proposal at a time and add new text-input fields to your needs.",
    },
    {
      heading: "Review before submission",
      text: `Thank you very much! <br/>Please review your answers below.`,
      note: "Your answers are only submitted when you press the button below.",
    },
  ];

  let questionTasks = [
    {
      text: "Please give the {#} problem in {task} a short name.",
      note: 'Clear titles such as "Invoice submission too slow" or "Clarification of credit notes".',
      type: "textArea",
      questionName: "problem_short_name",
    },
    {
      text: "What exactly bother you about this task in {task}?",
      note: "Describe only this one problem. You can add another problem afterwards.",
      type: "textArea",
      questionName: "problem_desc",
    },
    {
      text: "Optional: Do you have any ideas on how to solve the problem?",
      note: "You can leave it blank if you can't think of anything yet.",
      type: "textArea",
      questionName: "solution_idea",
    },
    {
      text: "How much effor does this task requires in {task}",
      note: "It is about how much time is spent in average on the task behind the problem described.",
      type: "textInput",
    },
  ];

  function setTotalSteps(problemTask, steps, dimensions, areas) {
    totalSteps =
      4 + selectedAreas.length + dimensions.length + problemTask.length;
    setTimeLeft(totalStep);
  }

  function setOptions(step, totalStep) {
    if (step <= 1) {
      options = [...(areas || [])].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
    } else if (step === 2) {
      // selectedTasks = selectedTasks.filter((selectedTask) =>
      //   selectedAreas.some((selectedArea) =>
      //     selectedArea.tasks.some(
      //       (taskInSelectedArea) => taskInSelectedArea.id === selectedTask
      //     )
      //   )
      // );
      options = [
        ...(areas.find(
          (area) => area.id === selectedAreas[[totalStep - step]]?.id,
        )?.tasks || []),
      ].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      );
    } else if (step === 4) {
      options = selectedTasks.map((task) => task.task);
    }
  }

  function setTimeLeft(totalStep) {
    if (step < 3) {
      timeLeft = "10–20 min";
      return;
    }
    const totalTime =
      selectedAreas.length * 60 +
      selectedTasks.length * (15 * dimensions.length + 90);
    let completedTime = 0;

    if (step === 3) {
      const dimensionIndex = Math.max(
        0,
        totalStep - (step + selectedAreas.length) + 1,
      );
      completedTime = dimensionIndex * selectedTasks.length * 15;
    } else if (step === 4) {
      completedTime = dimensions.length * selectedTasks.length * 15;
    } else if (step === 5) {
      const detailIndex = Math.max(
        0,
        totalStep - (step + selectedAreas.length + dimensions.length) + 2,
      );
      completedTime =
        dimensions.length * selectedTasks.length * 15 +
        selectedAreas.length * 60 +
        detailIndex * 90;
    } else if (step >= 6) {
      completedTime = totalTime;
    }

    const time = Math.max(0, totalTime - completedTime);
    timeLeft = `${Math.floor(time / 60)}min ${time % 60}s`;
  }

  function getAdditionalFocusTasks() {
    return selectedTasks.filter(
      (task) =>
        !task.problem &&
        task.task.name.toLowerCase().includes(focusTaskSearch.toLowerCase()),
    );
  }

  function setSelectedTask() {
    let currentSelectedTask = [];
    areas.forEach((area) => {
      area.tasks?.forEach((task) => {
        if (
          !currentSelectedTask.find(
            (selectedTask) => selectedTask.id === task.id,
          )
        )
          currentSelectedTask = [...currentSelectedTask, task];
      });
      selectedTasks = selectedTasks.filter((selectedTask) =>
        currentSelectedTask.find(
          (currentTask) => selectedTask.task.id === currentTask.id,
        ),
      );
      currentSelectedTask.forEach((currentTask) => {
        if (
          !selectedTasks.find(
            (selectedTask) => selectedTask.task.id === currentTask.id,
          )
        ) {
          selectedTasks = [
            ...selectedTasks,
            {
              task: currentTask,
              dimensions: {},
            },
          ];
        }
      });
    });
  }

  function setProblemAreas() {
    selectedTasks.forEach((task) => {
      Object.values(task.dimensions).forEach((dimension) => {
        if (dimension >= 4) {
          task.problem = true;
          return;
        }
      });
    });
    if (!selectedTasks.some((task) => task.problem) && selectedTasks.length) {
      selectedTasks[0].problem = true;
    }
    selectedTasks = [...selectedTasks];
  }

  function setQuestionText(text, task) {
    return text.split("{task}").join(task);
  }

  function setCurrentDimensionState() {
    currentDimensionState = 0;
    const dimensionIndex = totalStep - (step + selectedAreas.length) + 1;
    let currentDimension = dimensions[dimensionIndex]?.id;

    selectedTasks.forEach((selectedTask) => {
      if (selectedTask.dimensions[currentDimension]) currentDimensionState++;
    });
  }

  function getStepText(
    currentStep,
    currentTotalStep,
    language,
    currentSelectedAreas,
    currentSelectedTasks,
    currentDimensions,
  ) {
    currentNote = translateStep(
      language,
      currentStep,
      "note",
      steps[currentStep].note,
    );
    if (currentStep === 2) {
      let step2Text = translateStep(language, 2, "text", steps[2].text)
        .split("$department")
        .join(currentSelectedAreas[currentTotalStep - currentStep]?.name);
      currentText = step2Text;
    } else if (currentStep === 3) {
      const dimension =
        currentDimensions[
          [currentTotalStep - (currentStep + currentSelectedAreas.length) + 1]
        ];
      currentText = translateQuestion(language, dimension, "question");
      currentNote = translateStep(
        language,
        3,
        "note",
        getShortKpiNote(dimension),
      );
    } else if (currentStep === 4) {
      let step4text = translateStep(
        language,
        currentStep,
        "text",
        steps[currentStep].text,
      ).split("||");
      let note4text = translateStep(
        language,
        currentStep,
        "note",
        steps[currentStep].note,
      ).split("||");
      currentText = currentSelectedTasks.some((task) => task.problem === true)
        ? step4text[0]
        : step4text[1];
      currentNote = currentSelectedTasks.some((task) => task.problem === true)
        ? note4text[0]
        : note4text[1];
    } else
      currentText = translateStep(
        language,
        currentStep,
        "text",
        steps[currentStep].text,
      );
  }

  function getShortKpiNote(dimension) {
    const notes = {
      usefulness:
        "Consider how directly the task contributes to meaningful work outcomes.",
      annoyance:
        "Consider how frustrating or unnecessarily difficult the task feels.",
      repetitiveness:
        "Consider how often the same or very similar steps are repeated.",
      time_consumption: "Consider the total time this task typically requires.",
      consistency:
        "Consider how standardized and predictable the task process is.",
      error_rate:
        "Consider how easily mistakes occur or rework becomes necessary.",
      ai_readiness:
        "Consider whether clear rules and usable data make automation feasible.",
    };
    return (
      notes[dimension?.id] ||
      dimension?.note?.split(". ")[0] ||
      "Rate this aspect based on your daily experience."
    );
  }

  function getStepIntroTitle(
    currentStep,
    currentTotalStep,
    language,
    currentSelectedAreas,
    currentDimensions,
    currentProblemTask,
  ) {
    if (currentStep === 3) {
      return translateQuestion(
        language,
        currentDimensions[
          currentTotalStep - (currentStep + currentSelectedAreas.length) + 1
        ],
      );
    }

    if (currentStep === 5) {
      return (
        currentProblemTask[
          currentTotalStep -
            (currentStep +
              currentSelectedAreas.length +
              currentDimensions.length) +
            2
        ]?.name || ""
      );
    }

    return `${currentStep === 2 ? currentSelectedAreas[currentTotalStep - currentStep]?.name || "" : ""}${translateStep(language, currentStep, "heading", steps[currentStep].heading)}`;
  }

  function getStepIntroDescription(currentStep, fallbackText) {
    if (currentStep === 5) {
      return `<strong>${getCurrentProblemTaskArea()}</strong> ${t("taskDetails")}`;
    }

    return fallbackText;
  }

  function getStepIntroVariant(currentStep) {
    if (currentStep === 0) return "welcome";
    if (currentStep === 3) return "dimension";
    if (currentStep === 4) return "focus";
    if (currentStep === 5) return "detail";
    if (currentStep === 6) return "final";
    return "default";
  }
  function setDisabledNext(totalStep) {
    disabledNext = false;
    if (step === 1 && selectedAreas.length === 0) {
      disabledNext = true;
    } else if (step === 2) {
      disabledNext = !selectedAreas[totalStep - step].tasks.some((task) =>
        selectedTasks.some((selectedTask) => task.id === selectedTask.task.id),
      );
    } else if (step === 3 && selectedTasks.length - currentDimensionState > 0) {
      disabledNext = true;
    } else if (step === 5) {
      let currentTaskId =
        problemTask[
          totalStep - (step + selectedAreas.length + dimensions.length) + 2
        ].id;
      //selectedTasks[totalStep - (step + departments.length + dimensions.length) + 2]?.details
      disabledNext = !isTaskDetailsComplete(
        selectedTasks.find((task) => task.task.id === currentTaskId),
      );
    } else if (step === 6) {
      disabledNext = true;
    } else {
      disabledNext = false;
    }
  }

  function requestNextStep() {
    if (disabledNext) return;
    if (
      step === 5 &&
      !getCurrentProblemTask()?.details?.some(isProposalEnabled)
    ) {
      openNoProposalModal = true;
      return;
    }
    advanceSurvey();
  }

  function advanceSurvey() {
    if (reviewEditMode) {
      const dimensionIndex = totalStep - (step + selectedAreas.length) + 1;
      const detailIndex =
        totalStep - (step + selectedAreas.length + dimensions.length) + 2;
      if (step === 3 && dimensionIndex < dimensions.length - 1) {
        totalStep++;
      } else if (step === 5 && detailIndex < problemTask.length - 1) {
        totalStep++;
      } else {
        continueReviewEdit();
      }
      return;
    }
    totalStep++;
    if (step === 4 && problemTask.length === 0) {
      step = step + 2;
      return;
    }
    if (
      !(step === 2 && totalStep - step < selectedAreas.length) &&
      !(
        step === 3 &&
        totalStep - (step + selectedAreas.length) + 1 < dimensions.length
      ) &&
      !(
        step === 5 &&
        totalStep - (step + selectedAreas.length + dimensions.length) + 1 <
          problemTask.length - 1
      )
    ) {
      if (step === 3) setProblemAreas();
      step++;
    }
  }

  function scheduleAutoSave(
    currentStep,
    currentTotalStep,
    areasToSave,
    tasksToSave,
  ) {
    const signature = JSON.stringify([
      currentStep,
      currentTotalStep,
      areasToSave,
      tasksToSave,
    ]);
    if (signature === lastAutoSaveSignature) return;
    lastAutoSaveSignature = signature;
    clearTimeout(autoSaveTimer);
    autoSaveTimer = setTimeout(() => saveCurrentSurvey(false), 350);
  }

  function saveCurrentSurvey(showConfirmation = false) {
    if (previewMode) return;
    let saveCurrentSurvey = {
      idSurveyUser,
      selectedAreas,
      selectedTasks,
      departments,
      step,
      totalStep,
      totalSteps,
      currentDimensionState,
      timeSaved: timeSaved + Date.now() - timeStart,
      timeDetailTotal: timeDetailTotal,
    };
    let surveysProgress = JSON.parse(
      localStorage.getItem("surveyProgress") || "[]",
    );
    let existingIndex = surveysProgress.findIndex(
      (s) => s.idSurveyUser === idSurveyUser,
    );
    if (existingIndex >= 0) {
      surveysProgress[existingIndex] = saveCurrentSurvey;
    } else {
      surveysProgress.push(saveCurrentSurvey);
    }

    localStorage.setItem("surveyProgress", JSON.stringify(surveysProgress));
    openModalSaveAnswers = showConfirmation;
  }
  function loadSurveyData() {
    if (saved) {
      const data = JSON.parse(saved).find(
        (s) => s.idSurveyUser === idSurveyUser,
      );
      selectedAreas = data.selectedAreas || [];
      selectedTasks = data.selectedTasks || [];
      departments = data.departments || [];
      step = data.step || 0;
      totalSteps = data.totalSteps || 0;
      totalStep = data.totalStep || 0;
      currentDimensionState = data.currentDimensionState || 0;
      setProblemTask(selectedTasks);
      timeSaved = data.timeSaved;
      timeDetailTotal = data.timeDetailTotal;
    }
    timeStart = Date.now();
    openLoadModal = false;
  }

  function getValues(dimensions) {
    let values = [];
    Object.entries(dimensions).forEach(([key, value]) => {
      values.push({ question_id: key, value: value });
    });
    return values;
  }

  function sendAnswerd() {
    if (previewMode) {
      sentAnswerd = true;
      return;
    }
    if (saved) {
      let surveysProgress = JSON.parse(saved || "[]");
      let updatedSurveys = surveysProgress.filter(
        (s) => !(s.idSurveyUser === idSurveyUser),
      );
      localStorage.setItem("surveyProgress", JSON.stringify(updatedSurveys));
    }

    selectedTasks.forEach((task) => {
      if (task && task.dimensions && task.task) {
        const data = {
          task_survey_id: dataSurvey.survey_tasks.find(
            (ts) => ts.task.id === task.task.id,
          )?.id,
          values: getValues(task.dimensions),
          user_answer_id: userAnswersId,
        };
        if (problemTask.some((Ptask) => Ptask.id === task.task.id)) {
          data.details = task.details.map(
            ({ _proposal_enabled, ...detail }) => ({
              ...detail,
              problem_short_name:
                detail.problem_short_name || detail.problem_desc?.slice(0, 80),
            }),
          );
        }
        sendTask(data);
      }
    });
  }
  function updateSurveyUsers() {
    if (previewMode) return;
    fetchWithSession(`/api/knextractor/survey-users/${idSurveyUser}`, {
      method: "PATCH",
      body: JSON.stringify({
        survey_answered: true,
      }),
    });
  }

  function sendTask(data) {
    if (previewMode) return;
    fetchWrapper(`/api/knextractor/answers`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }).then((response) => {
      if (response.ok) {
        //openModalSendAnswers = true;
        sentAnswerd = true;
      } else {
        console.error("Error saving form");
      }
    });
  }

  function getSurvey() {
    fetchWithSession(`/api/knextractor/surveys/${idSurvey}`)
      .then((response) => response.json())
      .then((data) => {
        dataSurvey = data;
        status = data.status;
      });
  }

  function getBusinessRows() {
    fetchWithSession("/api/knextractor/business-units/")
      .then((response) => response.json())
      .then((data) => {
        businessUnits = data;
      });
  }

  function getDepartments() {
    fetchWithSession("/api/knextractor/departments")
      .then((response) => response.json())
      .then((data) => {
        departments = data;
      });
  }

  function getAreas() {
    fetchWithSession("/api/knextractor/areas")
      .then((response) => response.json())
      .then((data) => {
        areas = data;
      });
  }
  function getTasks() {
    fetchWithSession("/api/knextractor/tasks")
      .then((response) => response.json())
      .then((data) => {
        tasks = data;
      });
  }

  function setSurveyOptions() {
    tasks = tasks.filter((task) =>
      dataSurvey.survey_tasks.some(
        (surveyTask) => surveyTask.task.id === task.id,
      ),
    );

    areas = areas
      .map((area) => ({
        ...area,
        tasks: area.tasks
          ? area.tasks.filter((areaTask) =>
              tasks.some((task) => task.id === areaTask.id),
            )
          : [],
      }))
      .filter((area) => tasks.some((task) => task.area.id === area.id));
    if (!previewMode && surveyUserId) {
      const userDepartmentIds = departments
        .filter((department) =>
          department.users?.some((user) => user.id === surveyUserId),
        )
        .map((department) => department.id);
      areas = areas.filter((area) =>
        userDepartmentIds.includes(area.department.id),
      );
      tasks = tasks.filter((task) =>
        areas.some((area) => area.id === task.area.id),
      );
    }
    dimensions = dimensionsPossibilities.filter((dimension) =>
      dataSurvey.questions.includes(dimension.id),
    );
    allowCustomTask = dataSurvey.custom_tasks;
  }

  async function getUserAnswersId() {
    if (previewMode) return;
    let overallTime = {
      overall_filling_time: Math.floor(
        (timeSaved + Date.now() - timeStart) / 1000,
      ),
      details_filling_time: Math.floor(timeDetailTotal / 1000),
    };
    return fetchWrapper(`/api/knextractor/user-answers`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(overallTime),
    })
      .then((response) => response.json())
      .then((data) => {
        userAnswersId = data.data.id;
      });
  }

  function getSurveyUser() {
    if (previewMode) {
      getSurvey();
      getBusinessRows();
      getDepartments();
      getAreas();
      getTasks();
      return;
    }
    if (idSurveyUser)
      fetchWithSession("/api/knextractor/survey-users/" + idSurveyUser)
        .then((response) => response.json())
        .then((data) => {
          if (data.survey_answered === false) {
            idSurvey = data.survey_id;
            surveyUserId = data.user_id;
            getSurvey();
            getBusinessRows();
            getDepartments();
            getAreas();
            getTasks();
          } else if (data.survey_answered) {
            notAvailableMessage = t("alreadyAnswered");
          }
        });
  }

  function getQuestions() {
    fetchWithSession("/api/knextractor/questions/")
      .then((response) => response.json())
      .then((data) => {
        dimensionsPossibilities = data;
      });
  }

  onMount(() => {
    getQuestions();
    getSurveyUser();
    const autoSaveInterval = setInterval(() => {
      if (step > 0 && allowAnswer && !sentAnswerd && !previewMode)
        saveCurrentSurvey(false);
    }, 2000);
    const saveBeforeExit = () => {
      if (step > 0 && allowAnswer && !sentAnswerd && !previewMode)
        saveCurrentSurvey(false);
    };
    window.addEventListener("beforeunload", saveBeforeExit);
    return () => {
      clearInterval(autoSaveInterval);
      clearTimeout(autoSaveTimer);
      window.removeEventListener("beforeunload", saveBeforeExit);
    };
  });
</script>

<div class="survey_header">
  <Header
    section="Potential Scout"
    projectNameOverride={dataSurvey?.name || ""}
    projectHref={ROUTES.dam.root}
    area="Surveys"
    areaHref={ROUTES.knex.root}
    areaClickable={false}
    breadcrumb={previewMode ? t("preview") : t("survey")}
  ></Header>
</div>
{#if allowAnswer}
  <div class="maincontainer">
    <div
      class="canvas"
      class:detail_canvas={step === 5}
      class:welcome_canvas={step === 0 && !sentAnswerd}
    >
      <div
        class="survey_status step_{step} {sentAnswerd
          ? ' sent_answer'
          : ''} {step === 0 && !sentAnswerd ? 'welcome_step' : ''}"
      >
        <div
          class="survey_body {sentAnswerd
            ? ' not_available_text'
            : ''} {step === 0 ? 'welcome_content' : ''}"
        >
          {#if sentAnswerd}
            <h5>
              {previewMode ? t("previewCompleted") : t("thanks")}
            </h5>
            <p>{t("closeWindow")}</p>
          {:else}
            <SurveyStepIntro
              title={stepIntroTitle}
              description={stepIntroDescription}
              note={step !== 5 ? currentNote : ""}
              noteLabel={step === 0
                ? t("anonymous")
                : step === 6
                  ? ""
                  : t("note")}
              variant={stepIntroVariant}
              showNote={step !== 5 && step !== 6}
            >
              {#if steps[step].helpText}
                <Button
                  kind="secondary"
                  icon={INFO_ICON}
                  on:click={() => {
                    openModalHelp = true;
                  }}
                />
              {/if}
            </SurveyStepIntro>
            {#if step !== 5}
              {#if step === 6}
                <div class="review_summary">
                  <section class="review_card">
                    <div class="review_card_header">
                      <h6>{t("areas")}</h6>
                      <Button
                        icon={EDIT_ICON}
                        size="small"
                        kind="secondary"
                        iconDescription={t("edit")}
                        on:click={() => editReviewSection("areas")}
                      />
                    </div>
                    <div class="review_tags">
                      {#each selectedAreas as area}<span>{area.name}</span
                        >{/each}
                    </div>
                  </section>

                  <section class="review_card">
                    <div class="review_card_header">
                      <h6>{t("tasksRatings")}</h6>
                      <Button
                        icon={EDIT_ICON}
                        size="small"
                        kind="secondary"
                        iconDescription={t("edit")}
                        on:click={() => editReviewSection("tasks")}
                      />
                    </div>
                    {#each selectedTasks as selectedTask}
                      <div class="review_task">
                        <strong>{selectedTask.task.name}</strong>
                        <dl>
                          {#each Object.entries(selectedTask.dimensions) as [questionId, value]}
                            <div>
                              <dt>
                                {translateQuestion(
                                  surveyLanguage,
                                  dimensions.find(
                                    (dimension) => dimension.id === questionId,
                                  ),
                                )}
                              </dt>
                              <dd>{getAnswerLabel(questionId, value)}</dd>
                            </div>
                          {/each}
                        </dl>
                      </div>
                    {/each}
                  </section>

                  {#if problemTask.length > 0}
                    <section class="review_card">
                      <div class="review_card_header">
                        <h6>{t("improvementDetails")}</h6>
                        <Button
                          icon={EDIT_ICON}
                          size="small"
                          kind="secondary"
                          iconDescription={t("edit")}
                          on:click={() => editReviewSection("details")}
                        />
                      </div>
                      {#each selectedTasks.filter((task) => task.problem) as selectedTask}
                        <div class="review_task review_details">
                          <strong>{selectedTask.task.name}</strong>
                          {#if selectedTask.details?.[0]}
                            <div class="review_detail_entry">
                              <div class="review_detail_metrics">
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/event_repeat.svg')"
                                  ></i>{selectedTask.details[0]
                                    .task_occurrences_per_month || 0}
                                  {t("executionsShort")}</span
                                >
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/clock.svg')"
                                  ></i>{formatDuration(
                                    selectedTask.details[0]
                                      .task_duration_seconds ||
                                      Number(
                                        selectedTask.details[0]
                                          .task_duration_minutes || 0,
                                      ) * 60,
                                  )}
                                  {t("perExecution")}</span
                                >
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/user.svg')"
                                  ></i>{selectedTask.details[0]
                                    .number_of_colleagues || 0}
                                  {t("colleagues")}</span
                                >
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/hash.svg')"
                                  ></i>{selectedTask.details[0]
                                    .standard_case_share_percent ?? 0}% {t(
                                    "standardCasesShort",
                                  )}</span
                                >
                              </div>
                            </div>
                          {/if}
                          {#each (selectedTask.details || []).filter(isProposalEnabled) as detail}
                            <div class="review_detail_entry">
                              <div class="review_detail_metrics secondary">
                                {#each detail.related_topics || [] as topic}<span
                                    ><i
                                      style="--metric-icon: url('/img/assistant/icons/problem.svg')"
                                    ></i>{translateOption(
                                      surveyLanguage,
                                      topic,
                                    )}</span
                                  >{/each}
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/speed.svg')"
                                  ></i>{detail.impact_on_task_time}
                                  {t("impact")}</span
                                >
                                <span
                                  ><i
                                    style="--metric-icon: url('/img/assistant/icons/event repeat.svg')"
                                  ></i>{translateOption(
                                    surveyLanguage,
                                    detail.occurrence_frequency,
                                  )}</span
                                >
                              </div>
                              <p>{detail.problem_desc}</p>
                            </div>
                          {/each}
                        </div>
                      {/each}
                    </section>
                  {/if}
                </div>
              {/if}
            {/if}
            {#if step === 0}
              <SurveyEstimatedTime
                label={t("estimatedTime")}
                value={timeLeft}
              />
            {:else if step !== 6}
              <div class="survey_options">
                {#if step === 4}
                  <div class="focus_task_selection">
                    <h6>{t("highlightedTasks")}</h6>
                    <div class="highlighted_task_list">
                      {#each selectedTasks.filter((task) => task.problem) as selectedTask}
                        <div>
                          <span class="task_list_icon" aria-hidden="true"
                          ></span><strong title={selectedTask.task.name}
                            >{selectedTask.task.name}</strong
                          >
                          <button
                            type="button"
                            title={selectedTasks.filter((task) => task.problem)
                              .length === 1
                              ? "At least one highlighted task is required"
                              : "Remove from highlighted tasks"}
                            disabled={selectedTasks.filter(
                              (task) => task.problem,
                            ).length === 1}
                            on:click={() => {
                              if (
                                selectedTasks.filter((task) => task.problem)
                                  .length === 1
                              )
                                return;
                              selectedTask.problem = false;
                              selectedTasks = [...selectedTasks];
                            }}>×</button
                          >
                        </div>
                      {/each}
                    </div>
                    <div class="add_focus_task">
                      <Button
                        kind="secondary"
                        text={t("addTask")}
                        icon={PLUS_ICON}
                        iconLeft
                        on:click={() => {
                          focusTaskPage = 0;
                          openFocusTaskModal = true;
                        }}
                      />
                    </div>
                  </div>
                {:else if step === 1 || step === 2}
                  <div class="buttons_wrapper">
                    {#each options as option}
                      <div
                        class:option={true}
                        title={option.name}
                        class:selected={step === 1
                          ? selectedAreas.find((area) => area.id === option.id)
                          : step === 2
                            ? selectedTasks.find(
                                (task) => task.task.id === option.id,
                              )
                            : selectedTasks.find(
                                (task) => task.task.id === option.id,
                              ).problem}
                      >
                        <Button
                          text={option.name}
                          kind="secondary"
                          iconRight
                          icon={selectedAreas.find(
                            (area) => area.id === option.id,
                          ) ||
                          (step === 2 &&
                            selectedTasks.find(
                              (task) => task.task.id === option.id,
                            )) ||
                          (step === 4 &&
                            selectedTasks.find(
                              (tasks) => tasks.task.id === option.id,
                            ).problem)
                            ? step === 1 || step === 2
                              ? CHECK_DARK_ICON
                              : CHECKBOX_SELECTED_WHITE_ICON
                            : CHECKBOX_ICON}
                          on:click={() => {
                            if (step === 1) {
                              if (
                                selectedAreas.find(
                                  (topic) => topic.id === option.id,
                                )
                              ) {
                                selectedAreas = selectedAreas.filter(
                                  (t) => t.id !== option.id,
                                );
                                selectedTasks = selectedTasks.filter(
                                  (selectedTask) =>
                                    !option.tasks.some(
                                      (taskInSelectedArea) =>
                                        taskInSelectedArea.id ===
                                        selectedTask.task.id,
                                    ),
                                );
                              } else selectedAreas = [...selectedAreas, option];
                            } else if (step === 2) {
                              if (
                                selectedTasks.find(
                                  (task) => task.task.id === option.id,
                                )
                              ) {
                                selectedTasks = selectedTasks.filter(
                                  (t) => t.task.id !== option.id,
                                );
                              } else {
                                selectedTasks = [
                                  ...selectedTasks,
                                  {
                                    task: option,
                                    problem: false,
                                    dimensions: {},
                                    details: [
                                      {
                                        problem_short_name: "",
                                        problem_desc: null,
                                        solution_idea: null,
                                        avg_time: null,
                                        number_of_colleagues: null,
                                        occurrences_per_year: null,
                                        time_lost_minutes: null,
                                        task_occurrences_per_month: null,
                                        task_duration_minutes: null,
                                        task_duration_seconds: null,
                                        standard_case_share_percent: null,
                                        related_topics: [],
                                        impact_on_task_time: "",
                                        occurrence_frequency: "",
                                        _proposal_enabled: false,
                                      },
                                    ],
                                  },
                                ];
                              }
                            } else if (step === 4) {
                              selectedTasks.find(
                                (task) => task.task.id === option.id,
                              ).problem = !selectedTasks.find(
                                (task) => task.task.id === option.id,
                              ).problem;
                              selectedTasks = [...selectedTasks];
                            }
                            setDisabledNext(totalStep);
                          }}
                        />
                      </div>
                    {/each}
                  </div>
                {:else if step === 3}
                  {#each selectedTasks as selectedTask}
                    <div class="dimension_task">
                      <div
                        class="dimension_task_text"
                        title={selectedTask.task.name}
                      >
                        {selectedTask.task.name}
                      </div>
                      <div class="buttons_wrapper">
                        {#each dimensions[[totalStep - (step + selectedAreas.length) + 1]]?.answers || [] as possibility}
                          <div
                            class:option={true}
                            title={translateOption(
                              surveyLanguage,
                              possibility.answer,
                            )}
                            class:selected={selectedTask.dimensions[
                              dimensions[
                                totalStep - (step + selectedAreas.length) + 1
                              ].id
                            ] === possibility.value}
                          >
                            <Button
                              kind="secondary"
                              iconRight
                              icon={selectedTask.dimensions[
                                dimensions[
                                  totalStep - (step + selectedAreas.length) + 1
                                ].id
                              ] === possibility.value
                                ? CHECK_DARK_ICON
                                : CHECKBOX_ICON}
                              text={translateOption(
                                surveyLanguage,
                                possibility.answer,
                              )}
                              on:click={() => {
                                selectedTask.dimensions[
                                  dimensions[
                                    totalStep -
                                      (step + selectedAreas.length) +
                                      1
                                  ].id
                                ] = possibility.value;
                                selectedTasks = [...selectedTasks];
                                setCurrentDimensionState();
                                setDisabledNext(totalStep);
                              }}
                            />
                          </div>
                        {/each}
                      </div>
                    </div>
                  {/each}
                {:else if step === 5}
                  {#key totalStep}
                    {#each selectedTasks.find((task) => task.task.id === problemTask[totalStep - (step + selectedAreas.length + dimensions.length) + 2].id).details as detail, detailIndex}
                      <!-- {#each selectedTasks[totalStep - (step + selectedAreas.length + dimensions.length) + 2]?.details || [] as detail, detailIndex} -->
                      <div class="task_detail_wrapper">
                        <!--{#each questionTasks as question}
                       <div class="question_text">
                        <div>
                          <p>
                            {setQuestionText(
                              question.text,
                              problemTask[
                                totalStep -
                                  (step +
                                    selectedAreas.length +
                                    dimensions.length) +
                                  2
                              ].name
                            )}
                          </p>
                          <p class="task_note">note: {question.note}</p>
                        </div>
                        {#if question.questionName === "problem" && detailIndex > 0}
                          <Button
                            kind="secondary"
                            text="Remove problem"
                            on:click={() => {
                              let index =
                                totalStep -
                                (step +
                                  selectedAreas.length +
                                  dimensions.length) +
                                2;
                              selectedTasks[index].details = selectedTasks[
                                index
                              ].details.filter(
                                (filterDetail) => filterDetail.id !== detail.id
                              );
                              addDetailPressed = !addDetailPressed;
                              setDisabledNext(totalStep);
                            }}
                          />
                        {/if}
                      </div>
                      {#if question.type === "textArea"}
                        <TextArea
                          bind:value={detail[question.questionName]}
                          light
                          rows={1}
                          labelText="Pains and/or Suggestions"
                          placeholder="Space for your Text-Input"
                          maxCount={500}
                          style="resize: none; overflow: hidden;"
                          on:input={(e) => {
                            e.target.style.height = "auto";
                            e.target.style.height =
                              e.target.scrollHeight + "px";
                            setDisabledNext(totalStep);
                          }}
                        ></TextArea>
                      {:else}
                        <div>
                          <p>Avg. time per month on task:</p>
                          <div class="input_wrapper">
                            <NumberInput
                              hideSteppers
                              light
                              bind:value={detail.avg_time}
                              on:input={() => setDisabledNext(totalStep)}
                            ></NumberInput>
                          </div>
                        </div>
                        <div>
                          <p># of colleagues with same task:</p>
                          <div class="input_wrapper">
                            <NumberInput
                              hideSteppers
                              light
                              bind:value={detail.number_of_colleagues}
                              on:input={() => setDisabledNext(totalStep)}
                            ></NumberInput>
                          </div>
                        </div>
                      {/if} 
                      -->
                        {#if detailIndex === 0}
                          <section class="task_baseline_card">
                            <header>
                              <div>
                                <strong>{t("taskBaseline")}</strong>
                                <p>{t("baselineHelp")}</p>
                              </div>
                            </header>
                            <div class="task_baseline_grid">
                              <!-- svelte-ignore a11y-label-has-associated-control -->
                              <label>
                                <span class="baseline_field_label">
                                  <strong>{t("executionsMonth")}</strong>
                                  <small>{t("timesMonth")}</small>
                                </span>
                                <div class="input_wrapper">
                                  <NumberInput
                                    hideSteppers
                                    light
                                    min={1}
                                    bind:value={
                                      detail.task_occurrences_per_month
                                    }
                                    on:input={() =>
                                      setTimeout(
                                        () =>
                                          clampBaselineNumber(
                                            getCurrentProblemTask(),
                                            detail,
                                            "task_occurrences_per_month",
                                            1,
                                          ),
                                        0,
                                      )}
                                  ></NumberInput>
                                </div>
                              </label>
                              <label>
                                <span class="baseline_field_label">
                                  <strong>{t("durationExecution")}</strong>
                                  <small>min / sec</small>
                                </span>
                                <div class="input_wrapper duration_wrapper">
                                  <div
                                    class="duration_fields"
                                    class:invalid_duration={!detail.task_duration_seconds ||
                                      detail.task_duration_seconds <= 0}
                                  >
                                    <input
                                      type="number"
                                      min="0"
                                      value={detail.task_duration_seconds
                                        ? Math.floor(
                                            detail.task_duration_seconds / 60,
                                          )
                                        : ""}
                                      placeholder="min"
                                      aria-label={t("minutes")}
                                      on:input={(event) =>
                                        setTaskDurationPart(
                                          detail,
                                          "minutes",
                                          event.currentTarget,
                                        )}
                                    /><span>:</span><input
                                      type="number"
                                      min="0"
                                      max="60"
                                      value={detail.task_duration_seconds
                                        ? detail.task_duration_seconds % 60
                                        : ""}
                                      placeholder="sec"
                                      aria-label={t("seconds")}
                                      on:input={(event) =>
                                        setTaskDurationPart(
                                          detail,
                                          "seconds",
                                          event.currentTarget,
                                        )}
                                    />
                                  </div>
                                </div>
                              </label>
                              <!-- svelte-ignore a11y-label-has-associated-control -->
                              <label>
                                <span class="baseline_field_label">
                                  <strong>{t("colleaguesTask")}</strong>
                                  <small>{t("colleagues")}</small>
                                </span>
                                <div class="input_wrapper">
                                  <NumberInput
                                    hideSteppers
                                    light
                                    min={0}
                                    bind:value={detail.number_of_colleagues}
                                    on:input={() =>
                                      setTimeout(
                                        () =>
                                          clampBaselineNumber(
                                            getCurrentProblemTask(),
                                            detail,
                                            "number_of_colleagues",
                                            0,
                                          ),
                                        0,
                                      )}
                                  ></NumberInput>
                                </div>
                              </label>
                              <!-- svelte-ignore a11y-label-has-associated-control -->
                              <label>
                                <span class="baseline_field_label">
                                  <strong>{t("standardCases")}</strong>
                                  <small>%</small>
                                </span>
                                <div class="input_wrapper">
                                  <NumberInput
                                    hideSteppers
                                    light
                                    min={0}
                                    max={100}
                                    bind:value={
                                      detail.standard_case_share_percent
                                    }
                                    on:input={() =>
                                      setTimeout(
                                        () =>
                                          clampBaselineNumber(
                                            getCurrentProblemTask(),
                                            detail,
                                            "standard_case_share_percent",
                                            0,
                                            100,
                                          ),
                                        0,
                                      )}
                                  ></NumberInput>
                                </div>
                              </label>
                            </div>
                            {#if !isTaskBaselineComplete(detail)}
                              <div class="baseline_warning" role="status">
                                <strong>{t("numericFieldsTitle")}</strong>
                                <span>{t("numericFieldsWarning")}</span>
                              </div>
                            {/if}
                          </section>
                        {/if}

                        {#if isProposalEnabled(detail)}
                          <article class="improvement_idea_card">
                            <header>
                              <span>{detailIndex + 1}</span>
                              <strong>{t("improvementIdea")}</strong>
                              <Button
                                icon={CLOSE_ICON}
                                size="small"
                                kind="secondary"
                                iconDescription={t("deleteIdea")}
                                on:click={() =>
                                  removeImprovementDetail(
                                    getCurrentProblemTask(),
                                    detailIndex,
                                  )}
                              />
                            </header>
                            <div class="improvement_idea_content">
                              <!-- svelte-ignore a11y-label-has-associated-control -->
                              <label class="idea_description"
                                ><strong
                                  >{t("description")}
                                  <em
                                    class="required_tag"
                                    class:complete={detail.problem_desc?.trim()}
                                    >{t("required")}</em
                                  ></strong
                                ><TextArea
                                  bind:value={detail.problem_desc}
                                  light
                                  rows={6}
                                  labelText=""
                                  placeholder={t("descriptionPlaceholder")}
                                  style="resize: vertical;"
                                  on:input={() =>
                                    setTimeout(
                                      () => setDisabledNext(totalStep),
                                      0,
                                    )}
                                ></TextArea></label
                              >
                              <div class="idea_choices_column">
                                <div class="idea_field">
                                  <strong
                                    >{t("relatedTopics")}
                                    <em
                                      class="required_tag"
                                      class:complete={detail.related_topics
                                        ?.length}>{t("required")}</em
                                    ></strong
                                  >
                                  <div class="choice_buttons">
                                    {#each improvementTopics as topic}<button
                                        type="button"
                                        title={translateOption(
                                          surveyLanguage,
                                          topic,
                                        )}
                                        class:selected={detail.related_topics?.includes(
                                          topic,
                                        )}
                                        on:click={() =>
                                          toggleImprovementTopic(detail, topic)}
                                        >{translateOption(
                                          surveyLanguage,
                                          topic,
                                        )}</button
                                      >{/each}
                                  </div>
                                </div>
                                <div class="idea_rating_grid">
                                  <div class="idea_field">
                                    <strong
                                      >{t("impactTaskTime")}
                                      <em
                                        class="required_tag"
                                        class:complete={detail.impact_on_task_time}
                                        >{t("required")}</em
                                      ></strong
                                    >
                                    <div class="choice_buttons">
                                      {#each impactOptions as option}<button
                                          type="button"
                                          title={option}
                                          class:selected={detail.impact_on_task_time ===
                                            option}
                                          on:click={() => {
                                            detail.impact_on_task_time = option;
                                            updateDerivedDetail(detail);
                                            selectedTasks = [...selectedTasks];
                                            setDisabledNext(totalStep);
                                          }}>{option}</button
                                        >{/each}
                                    </div>
                                  </div>
                                  <div class="idea_field">
                                    <strong
                                      >{t("howOften")}
                                      <em
                                        class="required_tag"
                                        class:complete={detail.occurrence_frequency}
                                        >{t("required")}</em
                                      ></strong
                                    >
                                    <div class="choice_buttons">
                                      {#each frequencyOptions as option}<button
                                          type="button"
                                          title={translateOption(
                                            surveyLanguage,
                                            option,
                                          )}
                                          class:selected={detail.occurrence_frequency ===
                                            option}
                                          on:click={() => {
                                            detail.occurrence_frequency =
                                              option;
                                            updateDerivedDetail(detail);
                                            selectedTasks = [...selectedTasks];
                                            setDisabledNext(totalStep);
                                          }}
                                          >{translateOption(
                                            surveyLanguage,
                                            option,
                                          )}</button
                                        >{/each}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </article>
                        {/if}
                      </div>
                    {/each}
                    {#if getCurrentProblemTask().details.some(isProposalEnabled)}
                      <div class="add_improvement_button">
                        <Button
                          kind="secondary"
                          text={t("addIdea")}
                          icon={PLUS_ICON}
                          iconLeft
                          disabled={!isProposalComplete(
                            getLastProposal(getCurrentProblemTask()),
                          )}
                          on:click={() => {
                            if (
                              !isProposalComplete(
                                getLastProposal(getCurrentProblemTask()),
                              )
                            )
                              return;
                            addImprovementDetail(getCurrentProblemTask());
                          }}
                        />
                      </div>
                    {:else}
                      <div
                        class="add_improvement_button initial_improvement_button"
                      >
                        <Button
                          kind="secondary"
                          text={t("addProposal")}
                          icon={PLUS_ICON}
                          iconLeft
                          on:click={() =>
                            addImprovementDetail(getCurrentProblemTask())}
                        />
                      </div>
                    {/if}
                    <!-- {/each} -->
                  {/key}
                {/if}
              </div>
            {/if}

            <div class="step_button">
              <!-- {#if step === 2 && allowCustomTask}
            <Button
              text="Add New"
              kind="secondary"
              on:click={() => (openModalAddTask = true)}
            />
          {:else if step === 3} -->
              {#if step === 3}
                <div
                  class="dimension_state {currentDimensionState ===
                  selectedTasks.length
                    ? 'dimension_state_complete'
                    : ''}"
                >
                  {currentDimensionState}/{selectedTasks.length}
                  {t("answered")}
                </div>
                <!-- {:else if step === 5}
            <Button
              kind="secondary"
              text="Add New"
              icon="plus"
              iconRight
              on:click={() => {
                const currentTask =
                  problemTask[
                    totalStep -
                      (step + selectedAreas.length + dimensions.length) +
                      2
                  ].id;
                let cosa = selectedTasks.find(
                  (task) => task.task.id === currentTask
                );

                selectedTasks
                  .find((task) => task.task.id === currentTask)
                  .details.push({
                    problem_short_name: "",
                    problem_desc: "",
                    solution_idea: "",
                    avg_time: "",
                    number_of_colleagues: "",
                    occurrences_per_year: "",
                    time_lost_minutes: "",
                  });
                addDetailPressed = !addDetailPressed;
                setDisabledNext(totalStep);
              }}
            /> -->
              {/if}
            </div>
          {/if}
        </div>

        {#if !sentAnswerd}
          <div class="survey_footer">
            {#if step === 0}
              <div class="buttons_wrapper">
                <Button
                  text={t("startSurvey")}
                  kind="secondary"
                  on:click={() => {
                    if (
                      saved &&
                      JSON.parse(saved).find(
                        (s) => s.idSurveyUser === idSurveyUser,
                      )
                    ) {
                      openLoadModal = true;
                    } else {
                      timeStart = Date.now();
                      totalStep++;
                      step++;
                    }
                  }}
                />
                <!-- <Button text="Close Survey" kind="secondary" url="/app/knex"
              /> -->
              </div>
            {:else}
              <div class="step_footer">
                <!-- {#if step === 2 && allowCustomTask}
              <Button
                text="Add New"
                kind="secondary"
                on:click={() => (openModalAddTask = true)}
              />
            {:else if step === 3}
              <div
                class="dimension_state {currentDimensionState ===
                selectedTasks.length
                  ? 'dimension_state_complete'
                  : ''}"
              >
                {currentDimensionState}/{selectedTasks.length} {t("answered")}
              </div>
            {:else if step === 4}
              <Button
                kind="secondary"
                text="Add New"
                icon="plus"
                iconRight
                on:click={() => {
                  selectedTasks[
                    totalStep -
                      (step + departments.length + dimensions.length) +
                      2
                  ]?.details.push({
                    improvement: "",
                    estimation: "",
                    colleagues: "",
                  });
                  addDetailPressed = !addDetailPressed;
                }}
              /> -->
                {#if step !== 6}
                  <div class="status_progress">
                    <SurveyEstimatedTime
                      label={t("estimatedTime")}
                      value={timeLeft}
                    />
                  </div>
                {/if}
                {#if step !== 6}
                  <p class="step_count">
                    <span
                      >{t("step")}
                      {Math.min(totalStep + 1, totalSteps)}
                      {t("of")}
                      {totalSteps}</span
                    >
                  </p>
                {/if}
                <div class="buttons_wrapper">
                  <Button
                    icon={ARROW_LEFT_ICON}
                    size="small"
                    kind="secondary"
                    iconDescription="Previous question"
                    disabled={false}
                    on:click={() => {
                      if (step === 1) {
                        step = 0;
                        totalStep = 0;
                        return;
                      }
                      if (step === 6) {
                        reviewEditMode = false;
                        if (problemTask.length > 0) {
                          step = 5;
                          totalStep =
                            selectedAreas.length +
                            dimensions.length +
                            problemTask.length +
                            2;
                        } else {
                          step = 4;
                          totalStep =
                            selectedAreas.length + dimensions.length + 1;
                        }
                        return;
                      }
                      if (reviewEditMode) {
                        const dimensionIndex =
                          totalStep - (step + selectedAreas.length) + 1;
                        const detailIndex =
                          totalStep -
                          (step + selectedAreas.length + dimensions.length) +
                          2;
                        if (
                          (step !== 3 && step !== 5) ||
                          (step === 3 && dimensionIndex === 0) ||
                          (step === 5 && detailIndex === 0)
                        ) {
                          continueReviewEdit();
                          return;
                        }
                      }
                      if (step === 6 && problemTask.length === 0) {
                        step = step - 2;
                        totalStep--;
                        return;
                      }
                      if (step !== 1) {
                        if (
                          !(step === 2 && totalStep - step > 0) &&
                          !(
                            step === 3 &&
                            totalStep - (step + selectedAreas.length) + 1 > 0
                          ) &&
                          !(
                            step === 5 &&
                            totalStep -
                              (step +
                                selectedAreas.length +
                                dimensions.length) +
                              2 >
                              0
                          )
                        )
                          step--;
                        totalStep--;
                      }
                    }}
                  />
                  {#if step === 6}
                    <Button
                      kind="primary"
                      text={t("submitSurvey")}
                      disabled={!isSurveyComplete()}
                      on:click={() => {
                        if (!isSurveyComplete()) return;
                        if (previewMode) sendAnswerd();
                        else
                          getUserAnswersId().then(() => {
                            sendAnswerd();
                          });
                      }}
                    />
                  {:else}
                    <Button
                      icon={ARROW_RIGHT_ICON}
                      size="small"
                      kind="secondary"
                      iconDescription="Next question"
                      disabled={disabledNext}
                      on:click={requestNextStep}
                    />
                  {/if}
                </div>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
    <SurveyRandomAnswerButton
      visible={isAdmin && step < 6 && !sentAnswerd}
      on:click={answerRandomAndContinue}
    />
  </div>
{:else}
  <div class="maincontainer">
    <div class="canvas">
      <div class="survey_status not_available">
        <div class="survey_body not_available_text">
          {@html notAvailableMessage}
        </div>
      </div>
    </div>
  </div>
{/if}

<Modal
  bind:open={openNoProposalModal}
  actions
  width={680}
  preventCloseOnClickOutside
  modalHeading={t("proposalTitle")}
  primaryButtonText={t("addNow")}
  secondaryButtonText={t("continueWithout")}
  secondaryButtonRight
  modalClass="proposal_modal"
  primaryButtonAction={() => {
    openNoProposalModal = false;
    addImprovementDetail(getCurrentProblemTask());
  }}
  secondaryButtonAction={() => {
    openNoProposalModal = false;
    advanceSurvey();
  }}
>
  <div class="proposal_prompt">
    <span class="proposal_prompt_icon" aria-hidden="true"></span>
    <p>{t("proposalBody")}</p>
  </div>
</Modal>

<Modal
  bind:open={openModalHelp}
  preventCloseOnClickOutside
  autowidth
  modalHeading={t("help")}
>
  <p>{currentNote}</p>
</Modal>

<Modal
  bind:open={openModalAddTask}
  preventCloseOnClickOutside
  autowidth
  actions
  primaryButtonDisabled={!newTask || tasks.find((t) => t.name === newTask)}
  modalHeading={`${t("addTask")} – ${selectedAreas[totalStep - step]?.name || t("selectedArea")}`}
  primaryButtonText={t("confirm")}
  secondaryButtonText={t("cancel")}
  primaryButtonAction={() => {
    createNewTask();
    // if (!newTask || tasks.find((t) => t.name === newTask)) return;
    // let id = `task${tasks.length + 1}`;
    // tasks = [...tasks, { id: id, text: newTask }];
    // areas[totalStep - step].tasks.push({ id: id, text: newTask });
    // setOptions(step, totalStep);
    // newTask = "";
    // openModalAddTask = false;
  }}
>
  <TextInput bind:value={newTask} autocomplete="on" size="sm" />
</Modal>

<Modal
  bind:open={openLoadModal}
  actions
  autowidth
  preventCloseOnClickOutside
  modalHeading={t("loadSaved")}
  primaryButtonText={t("confirm")}
  secondaryButtonText={t("cancel")}
  primaryButtonAction={loadSurveyData}
  secondaryButtonAction={() => {
    openLoadModal = false;
    timeStart = Date.now();
    totalStep++;
    step++;
  }}
>
  <h5>{t("loadQuestion")}</h5>
  <p>{t("loadWarning")}</p>
</Modal>

<Modal
  bind:open={openFocusTaskModal}
  modalHeading={t("addTask")}
  modalClass="focus-task-modal-shell"
  width={440}
  actions
  primaryButtonText={t("addTask")}
  secondaryButtonText={t("cancel")}
  primaryButtonAction={() => {
    openFocusTaskModal = false;
    selectedTasks = [...selectedTasks];
  }}
  secondaryButtonAction={() => (openFocusTaskModal = false)}
>
  <div class="focus_task_modal">
    <p>{t("chooseTask")}</p>
    <input
      value={focusTaskSearch}
      on:input={(event) => {
        focusTaskSearch = event.currentTarget.value;
        focusTaskPage = 0;
      }}
      placeholder={t("searchTasks")}
      aria-label={t("searchTasks")}
    />
    <div>
      {#each getAdditionalFocusTasks().slice(focusTaskPage * 3, focusTaskPage * 3 + 3) as selectedTask}
        <button
          type="button"
          title={selectedTask.task.name}
          on:click={() => {
            selectedTask.problem = true;
            selectedTasks = [...selectedTasks];
            if (
              focusTaskPage > 0 &&
              focusTaskPage * 3 >= getAdditionalFocusTasks().length
            )
              focusTaskPage--;
          }}><span></span><strong>{selectedTask.task.name}</strong></button
        >
      {/each}
    </div>
    {#if getAdditionalFocusTasks().length > 3}
      <div class="focus_task_pagination">
        <button
          type="button"
          disabled={focusTaskPage === 0}
          on:click={() => focusTaskPage--}>←</button
        >
        <span
          >{focusTaskPage * 3 + 1}–{Math.min(
            focusTaskPage * 3 + 3,
            getAdditionalFocusTasks().length,
          )} of {getAdditionalFocusTasks().length}</span
        >
        <button
          type="button"
          disabled={(focusTaskPage + 1) * 3 >= getAdditionalFocusTasks().length}
          on:click={() => focusTaskPage++}>→</button
        >
      </div>
    {/if}
  </div>
</Modal>
<!-- <Modal
  open={openModalSendAnswers}
  modalHeading="Sent answer"
  preventCloseOnClickOutside
  autowidth
>
  <!-- <h5>Sent answer.</h5> -->
<!-- <p>Thank you for your collaboration.</p>
</Modal> -->

<Modal
  bind:open={openModalSaveAnswers}
  modalHeading={t("saveStatus")}
  preventCloseOnClickOutside
  autowidth
>
  <!-- <h5>Sent answer.</h5> -->
  <p>{t("continueLater")}</p>
</Modal>

<style>
  .proposal_prompt {
    width: 100%;
    display: grid;
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: flex-start;
    gap: 1.25rem;
    padding: 0.55rem 0 0.1rem;
  }

  .proposal_prompt_icon {
    width: 2.5rem;
    height: 2.5rem;
    display: block;
    border-radius: 50%;
    background: #fff1ea;
    position: relative;
  }

  .proposal_prompt_icon::after {
    content: "";
    position: absolute;
    inset: 0.65rem;
    background: var(--pax-orange);
    mask: url("/img/assistant/icons/plus.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/plus.svg") center / contain
      no-repeat;
  }

  .proposal_prompt p {
    margin: 0;
    color: #303846;
    max-width: none;
    font-size: 1rem;
    line-height: 1.6;
    letter-spacing: 0;
  }

  :global(.proposal_modal .buttons-container) {
    margin-top: 1.35rem;
  }

  :global(.proposal_modal .actions-buttons-container) {
    gap: 0.75rem;
  }

  :global(.proposal_modal .actions-buttons-container button) {
    min-width: 9.5rem;
    width: auto;
    white-space: nowrap;
  }

  .canvas {
    height: auto;
    border-radius: 0.5rem;
    gap: 1rem;
    width: min(64rem, calc(100vw - 6rem));
    display: flex;
    flex-direction: column;
  }

  .canvas.welcome_canvas {
    width: min(34rem, calc(100vw - 6rem));
    max-width: min(34rem, calc(100vw - 6rem));
    height: auto;
    max-height: calc(100vh - 8rem);
  }

  .maincontainer {
    border-radius: 0.5rem;
    width: 100%;
    min-height: calc(100vh - 5rem);
    padding: 1rem 2rem;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    overflow-y: auto;
  }

  .survey_status {
    box-sizing: border-box;
    margin-inline: auto;
    padding: 2.5rem;
    background-color: var(--pax-white);
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
  }

  .task_note {
    color: var(--pax-grey);
    font-size: 0.9rem;
  }

  .buttons_wrapper {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
  }

  .dimension_task .buttons_wrapper {
    width: 100%;
    gap: 0.5rem;
  }

  .step_footer {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 1.5rem;
  }

  .step_footer > .buttons_wrapper {
    gap: 0.35rem;
    justify-content: flex-end;
  }

  .survey_options {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 1rem;
    padding: 1rem 0;
  }

  :global(
      .survey_status
        .survey_body
        .survey_options
        .buttons_wrapper
        .option:not(.selected)
        .mainbutton
    ),
  :global(
      .survey_status .survey_footer .buttons_wrapper a.mainbutton.secondary
    ) {
    color: var(--pax-black);
    background-color: var(--pax-white);
  }
  :global(
      .survey_status
        .survey_body
        .survey_options
        .buttons_wrapper
        .option
        .mainbutton
    ),
  :global(
      .survey_status .survey_footer .buttons_wrapper a.mainbutton.secondary
    ) {
    border: 1px solid var(--pax-black);
  }
  .dimension_task {
    display: flex;
  }

  .dimension_task_text {
    width: 6rem;
    padding-right: 0.5rem;
    overflow: hidden;
    align-content: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.7rem;
  }

  .dimension_state {
    background-color: var(--pax-grey);
    border-radius: 0.5rem;
    align-content: center;
    padding: 0.5rem;
    width: 7rem;
    color: var(--pax-white);
    text-align: center;
    height: 3rem;
  }

  .dimension_state_complete {
    background-color: #4cc07a !important;
  }

  .task_detail_wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  .task_detail_wrapper:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  .task_detail_wrapper div {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  /* .task_detail_wrapper div p {
    width: 15rem;
  } */

  :global(.task_detail_wrapper .bx--form-item) {
    flex-direction: column-reverse;
  }

  :global(
      .task_detail_wrapper
        .bx--form-item
        .bx--text-area__label-wrapper
        .bx--label
    ) {
    color: var(--pax-black-inactive);
  }

  :global(
      .task_detail_wrapper .bx--form-item .bx--text-area__wrapper textarea
    ) {
    border-bottom: 0;
  }

  .done_step {
    padding-top: 0.5rem;
  }

  .review_summary {
    display: grid;
    gap: 0.75rem;
    padding: 0.75rem 0 1rem;
  }

  .review_card {
    border: 1px solid #dfe3e8;
    border-radius: 0.5rem;
    padding: 1rem;
    background: var(--pax-white);
  }

  .review_card_header,
  .review_task dl div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .review_card_header {
    align-items: center;
  }

  .review_card_header :global(button) {
    flex: 0 0 auto;
  }

  .review_tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding-top: 0.75rem;
  }

  .review_tags span {
    border-radius: 1rem;
    padding: 0.35rem 0.65rem;
    border: 1px solid #dfe3e8;
    background: #fff;
  }

  .review_task {
    padding-top: 0.75rem;
    margin-top: 0.75rem;
  }

  .review_task dl {
    margin: 0.5rem 0 0;
  }

  .review_task dt,
  .review_details p {
    color: var(--pax-grey);
  }

  .review_task dd {
    margin: 0;
    text-align: right;
    font-weight: 600;
  }

  :global(.review_card > .mainbutton) {
    margin-top: 0.75rem;
    height: 2.5rem !important;
    padding: 0.55rem 0.8rem !important;
    font-size: 0.75rem;
  }

  .question_text {
    display: flex;
    justify-content: space-between;
  }

  .question_text div {
    display: block;
  }

  .question_text div p {
    width: auto;
  }

  .sent_answer {
    justify-content: center;
    align-items: center;
  }

  .not_available {
    justify-content: center;
    align-items: center;
    background-color: var(--pax-white);
    background-image: url("/img/assistant/icons/forbidden.svg");
    background-size: calc(100% - 4rem) calc(100% - 8rem);
    background-position: center;
    background-repeat: no-repeat;
    padding: 1rem;
  }

  .not_available_text {
    font-size: 3rem;
    text-align: center;
  }
  .maincontainer {
    background-color: var(--pax-smoke);
    border-radius: 0;
  }

  :global(.survey_header .header_wrapper) {
    left: 0 !important;
    width: 100% !important;
  }

  :global(.survey_header .header_wrapper .header_breadcrumb) {
    margin-left: 65px;
  }

  :global(
      .survey_status
        .survey_footer
        .buttons_wrapper
        .mainbutton.secondary:not(.disabled):hover
    ),
  :global(
      .survey_header
        .header_wrapper
        .header_functions
        .mainbutton.secondary:not(.disabled):hover
    ),
  :global(
      .survey_status
        .survey_body
        .survey_options
        .selected
        .mainbutton.secondary:hover
    ) {
    background-color: var(--pax-orange);
  }

  :global(
      .survey_status
        .survey_body
        .survey_options
        .selected
        .mainbutton.secondary:hover
    ) {
    border: 1px solid var(--pax-orange);
  }

  :global(
      .survey_status
        .survey_body
        .survey_options
        .task_detail_wrapper
        .input_wrapper
        .bx--form-item
        .bx--number
        .bx--form-requirement
    ) {
    display: none;
  }

  :global(.task_detail_wrapper .bx--number input[type="number"]) {
    min-width: 2rem;
    width: 3rem;
    padding-right: 0;
  }
  .task_detail_wrapper .input_wrapper p {
    width: 5rem;
  }

  .task_baseline_card,
  .improvement_idea_card {
    width: 100%;
    box-sizing: border-box;
    padding: 1rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.65rem;
    background: #fff;
  }

  .task_baseline_card header,
  .task_baseline_card header div,
  .improvement_idea_card > header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 0.65rem;
  }

  .task_baseline_card header div {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.2rem;
  }

  .task_baseline_card header p {
    margin: 0;
    color: var(--pax-black-inactive);
    font-size: 0.7rem;
  }

  .task_baseline_grid {
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0.85rem;
  }

  .task_baseline_grid label {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .baseline_field_label {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-height: 2.35rem;
  }

  .baseline_field_label strong {
    color: #2f3540;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.25;
  }

  .baseline_field_label small {
    color: #68717d;
    font-size: 0.68rem;
    font-weight: 500;
    line-height: 1.2;
  }

  .task_baseline_grid .input_wrapper {
    display: block;
    align-items: center;
    justify-content: start;
  }

  :global(.task_baseline_grid .bx--form-item),
  :global(.task_baseline_grid .bx--number input[type="number"]) {
    width: 6rem;
    min-width: 6rem;
  }

  .baseline_warning {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    margin: 0.85rem 0 0;
    padding: 0.65rem 0.75rem;
    border: 1px solid #c97982;
    border-radius: 0.5rem;
    background: #fbebed;
    color: #8f3d46;
    line-height: 1.35;
  }

  .baseline_warning strong {
    font-size: 0.78rem;
    font-weight: 700;
  }

  .baseline_warning span {
    font-size: 0.74rem;
  }

  .duration_time_input {
    width: 100%;
    min-width: 0;
    height: 2.5rem;
    box-sizing: border-box;
    padding: 0.65rem 0.75rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.35rem 0 0 0.35rem;
    background: #fff;
    color: var(--pax-black);
    font: inherit;
  }

  .duration_time_input:focus {
    border-color: var(--pax-orange);
    outline: 1px solid var(--pax-orange);
  }

  .duration_time_input.invalid_duration {
    border-color: #da1e28;
    outline: 1px solid #da1e28;
  }

  .duration_fields {
    display: grid !important;
    grid-template-columns: minmax(2.8rem, 1fr) auto minmax(2.8rem, 1fr);
    align-items: center !important;
    gap: 0.25rem;
    width: 100%;
    height: 2.5rem;
    box-sizing: border-box;
    padding: 0 0.45rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.35rem 0 0 0.35rem;
    background: #fff;
  }

  .duration_fields.invalid_duration {
    border-color: #da1e28;
    outline: 1px solid #da1e28;
  }

  .duration_fields input {
    width: 100%;
    min-width: 0;
    height: 2rem;
    box-sizing: border-box;
    padding: 0.35rem;
    border: 0;
    background: transparent;
    color: var(--pax-black);
    font: inherit;
    text-align: center;
  }

  .duration_fields input:focus {
    outline: 0;
  }

  .duration_fields span {
    color: #737b85;
    font-weight: 700;
  }

  .improvement_idea_card > header > span {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.45rem;
    background: #f1f3f5;
    font-weight: 700;
  }

  .improvement_idea_card > header :global(button) {
    margin-left: auto;
    width: 2rem;
    height: 2rem;
    border: 0;
    border-radius: 0.4rem;
    background: var(--pax-black);
    color: #fff;
    cursor: pointer;
  }

  .idea_description,
  .idea_field {
    display: flex;
    flex-direction: column;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    gap: 0.5rem;
    margin-top: 0.85rem;
    text-align: left;
    width: 100%;
  }

  .idea_field strong,
  .idea_description > strong {
    font-size: 0.7rem;
  }

  .idea_field em,
  .idea_description em {
    padding: 0.25rem 0.55rem;
    border: 1px solid #c97982;
    border-radius: 1rem;
    background: var(--pax-red);
    color: #8f3d46;
    font-size: 0.65rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.1;
  }

  .idea_field em.complete,
  .idea_description em.complete {
    border-color: #7eb889;
    background: var(--pax-green);
    color: #28713a;
  }

  .choice_buttons {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    flex-wrap: wrap;
    gap: 0.45rem;
    width: 100%;
  }

  .choice_buttons button {
    max-width: 100%;
    padding: 0.5rem 0.7rem;
    overflow: hidden;
    border: 1px solid #cfd4da;
    border-radius: 0.4rem;
    background: #fff;
    color: var(--pax-black);
    font-size: 0.68rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .choice_buttons button.selected {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: #fff;
  }

  .idea_rating_grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .add_improvement_button {
    align-self: flex-end;
  }

  .survey_status.welcome_step {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 2.5rem;
    overflow: hidden;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .welcome_content {
    width: 100%;
    padding: 0;
  }

  .welcome_step > .survey_footer {
    padding: 0;
    margin-top: 1.5rem;
  }

  .welcome_step > .survey_footer .buttons_wrapper {
    width: 100%;
  }

  :global(.welcome_step > .survey_footer button) {
    width: 100% !important;
    min-width: 16rem;
  }

  .survey_status.step_1 {
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .survey_status.step_2 {
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .step_1 .survey_options,
  .step_2 .survey_options {
    min-height: auto;
    padding: 0.75rem 0 1.5rem;
  }

  .step_1 .survey_options > .buttons_wrapper,
  .step_2 .survey_options > .buttons_wrapper {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.8rem 1rem;
    width: 100%;
  }

  .step_1 .survey_options .option,
  .step_2 .survey_options .option {
    min-width: 0;
  }

  :global(.step_1 .survey_options .option .mainbutton),
  :global(.step_2 .survey_options .option .mainbutton) {
    width: 100%;
    min-width: 0;
    min-height: 3rem;
    box-sizing: border-box;
    justify-content: space-between;
    padding: 0.65rem 1rem !important;
    border: 1px solid #dfe3e8 !important;
    border-radius: 0.5rem;
    background: #fff !important;
    color: var(--pax-black) !important;
  }

  :global(.step_1 .survey_options .option .button_text),
  :global(.step_2 .survey_options .option .button_text) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.step_1 .survey_options .option .icon),
  :global(.step_2 .survey_options .option .icon) {
    min-width: 1rem;
    flex: 0 0 auto;
  }

  :global(.step_1 .survey_options .option.selected .mainbutton),
  :global(.step_2 .survey_options .option.selected .mainbutton) {
    border-color: var(--pax-black) !important;
    background: #fff !important;
  }

  :global(.step_1 .survey_options .option.selected .mainbutton.secondary:hover),
  :global(
      .step_2 .survey_options .option.selected .mainbutton.secondary:hover
    ) {
    border-color: var(--pax-black) !important;
    background: #fff !important;
  }

  :global(.step_1 .survey_options .option.selected .icon),
  :global(.step_2 .survey_options .option.selected .icon) {
    width: 1rem;
    height: 1rem;
  }

  :global(.step_1 .survey_options .option.selected .icon img),
  :global(.step_2 .survey_options .option.selected .icon img) {
    display: block;
    width: 1rem;
    height: 1rem;
  }

  .step_1 > .survey_footer {
    padding-top: 1.4rem;
  }

  .step_1 .step_footer {
    align-items: center;
  }

  :global(.step_1 .step_footer .buttons_wrapper button) {
    width: 3rem;
    height: 3rem;
    border-radius: 0.5rem;
  }

  .survey_status.step_3 {
    width: min(68rem, 100%);
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .step_3 .survey_options {
    min-height: auto;
    justify-content: flex-start;
    gap: 0.8rem;
    padding: 0.25rem 0 1rem;
  }

  .step_3 .dimension_task {
    display: grid;
    grid-template-columns: minmax(9rem, 1.4fr) minmax(30rem, 4fr);
    align-items: center;
    min-height: 3rem;
  }

  .step_3 .dimension_task_text {
    width: auto;
    padding: 0 1rem 0 0;
    color: #252a31;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .step_3 .dimension_task .buttons_wrapper {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.8rem;
    height: 100%;
  }

  :global(.step_3 .dimension_task .option .mainbutton) {
    width: 100%;
    min-width: 0;
    height: 100%;
    min-height: 3rem;
    justify-content: space-between;
    padding: 0.65rem 1rem !important;
    border: 1px solid #dfe3e8 !important;
    border-radius: 0.5rem;
    background: #fff !important;
    color: var(--pax-black) !important;
  }

  :global(.step_3 .dimension_task .option .button_text) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.step_3 .dimension_task .option.selected .mainbutton) {
    border-color: var(--pax-black) !important;
    background: #fff !important;
    color: var(--pax-black) !important;
    box-shadow: none;
  }

  :global(
      .step_3 .dimension_task .option.selected .mainbutton.secondary:hover
    ) {
    border-color: var(--pax-black) !important;
    background: #fff !important;
  }

  :global(.step_3 .dimension_task .option .icon) {
    min-width: 1rem;
    flex: 0 0 auto;
  }

  :global(.step_3 .dimension_task .option.selected .icon),
  :global(.step_3 .dimension_task .option.selected .icon img) {
    width: 1rem;
    height: 1rem;
  }

  .step_3 .dimension_state {
    width: max-content;
    height: auto;
    margin-top: 1rem;
    padding: 0.45rem 0.75rem;
    border: 1px solid #c97982;
    border-radius: 1rem;
    background: var(--pax-red);
    color: #8f3d46;
    font-size: 0.7rem;
  }

  .step_3 .dimension_state.dimension_state_complete {
    border: 1px solid #7eb889;
    background: var(--pax-green) !important;
    color: #28713a !important;
  }

  .step_3 > .survey_footer {
    padding-top: 1.25rem;
  }

  .survey_status.step_4 {
    width: min(38rem, 100%);
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .step_4 .survey_options {
    min-height: auto;
    padding: 0;
  }

  .focus_task_selection {
    width: 100%;
  }

  .focus_task_selection h6 {
    margin: 0 0 0.75rem;
    font-size: 0.86rem;
  }

  .highlighted_task_list {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .highlighted_task_list > div {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    min-height: 3rem;
    box-sizing: border-box;
    padding: 0.65rem 0.9rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.5rem;
    background: #fff;
  }

  .task_list_icon {
    display: block;
    width: 1rem;
    height: 1rem;
    flex: 0 0 auto;
    background: var(--pax-orange);
    mask: url("/img/assistant/icons/task.svg") center / contain no-repeat;
    -webkit-mask: url("/img/assistant/icons/task.svg") center / contain
      no-repeat;
  }

  .highlighted_task_list strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.75rem;
  }
  .highlighted_task_list button {
    display: grid;
    width: 1.65rem;
    height: 1.65rem;
    margin-left: auto;
    flex: 0 0 auto;
    place-items: center;
    border: 1px solid #d7dbe0;
    border-radius: 0.35rem;
    background: #fff;
    color: #626a74;
    cursor: pointer;
  }

  .add_focus_task {
    margin-top: 0.8rem;
  }

  .add_focus_task :global(.mainbutton) {
    height: 2.75rem !important;
    min-width: 0;
  }

  .step_4 > .survey_footer {
    padding-top: 1.25rem;
  }

  .focus_task_modal > p {
    max-width: 100%;
    color: #59616c;
    line-height: 1.5;
  }

  :global(.focus-task-modal-shell .bx--modal-container) {
    width: min(32rem, calc(100vw - 2rem)) !important;
    max-width: min(32rem, calc(100vw - 2rem)) !important;
  }

  :global(.focus-task-modal-shell .modal_wrapper) {
    width: 100% !important;
    box-sizing: border-box;
  }

  .focus_task_modal > input {
    width: 100%;
    box-sizing: border-box;
    margin: 0.6rem 0 0.8rem;
    padding: 0.8rem 1rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.45rem;
  }

  .focus_task_modal > div {
    display: flex;
    max-height: 20rem;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: auto;
  }

  .focus_task_modal button {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #dfe3e8;
    border-radius: 0.45rem;
    background: #fff;
    text-align: left;
    cursor: pointer;
  }

  .focus_task_modal button.selected {
    border-color: var(--pax-orange);
    background: #fff8f5;
  }

  .focus_task_modal button span {
    display: grid;
    width: 1rem;
    height: 1rem;
    flex: 0 0 auto;
    place-items: center;
    border: 1px solid #aeb5bf;
    color: var(--pax-orange);
    font-size: 0.7rem;
  }

  .focus_task_modal button strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .focus_task_modal > .focus_task_pagination {
    display: grid;
    grid-template-columns: 2.25rem 1fr 2.25rem;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
    overflow: visible;
  }

  .focus_task_pagination span {
    text-align: center;
    color: #59616c;
    font-size: 0.7rem;
  }

  .focus_task_modal .focus_task_pagination button {
    display: grid;
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    place-items: center;
    background: var(--pax-black);
    color: #fff;
  }

  .focus_task_modal .focus_task_pagination button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .canvas.detail_canvas {
    width: min(calc(100vw - 7rem), 76rem);
    height: auto;
  }

  .survey_status.step_5 {
    width: 100%;
    min-height: auto;
    box-sizing: border-box;
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
    justify-content: flex-start;
    gap: 1rem;
  }

  .step_5 .survey_options {
    min-height: auto;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 0;
  }

  .step_5 .task_detail_wrapper {
    width: min(70rem, 100%);
    max-width: 70rem;
    gap: 1rem;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .step_5 .task_baseline_card,
  .step_5 .improvement_idea_card {
    padding: 1rem 1.15rem;
    border-color: #dfe3e8;
    border-radius: 0.55rem;
  }

  .step_5 .task_baseline_card header strong,
  .step_5 .improvement_idea_card > header strong {
    font-size: 0.86rem;
  }

  .step_5 .task_baseline_card header p {
    font-size: 0.74rem;
  }

  .step_5 .task_baseline_grid {
    grid-template-columns: repeat(4, minmax(8.5rem, 1fr));
    gap: 0.9rem;
    margin-top: 1rem;
  }

  .step_5 .task_baseline_grid label {
    gap: 0.55rem;
    color: #414852;
  }

  .step_5 .task_baseline_grid .input_wrapper {
    width: 100%;
    max-width: 11rem;
  }

  :global(.step_5 .task_baseline_grid .bx--form-item),
  :global(.step_5 .task_baseline_grid .bx--number input[type="number"]) {
    width: 100%;
    min-width: 7rem;
  }

  .step_5 .improvement_idea_card > header {
    padding-bottom: 0.5rem;
  }

  .step_5 .improvement_idea_content {
    display: grid !important;
    grid-template-columns: minmax(18rem, 0.9fr) minmax(24rem, 1.1fr);
    align-items: start !important;
    gap: 1.5rem;
  }

  .step_5 .idea_choices_column {
    display: flex !important;
    flex-direction: column;
    align-items: stretch !important;
    gap: 0.8rem;
  }

  .step_5 .idea_description,
  .step_5 .idea_field {
    margin-top: 0.75rem;
  }

  :global(.step_5 .idea_description textarea) {
    min-height: 6rem;
    box-sizing: border-box;
    border: 1px solid #dfe3e8 !important;
    border-radius: 0.4rem;
    background: #fff;
  }

  .step_5 .choice_buttons {
    gap: 0.55rem;
  }

  .step_5 .choice_buttons button {
    min-height: 2.35rem;
    padding: 0.5rem 0.85rem;
    border-color: var(--pax-black);
    background: #fff;
    font-size: 0.7rem;
  }

  .step_5 .choice_buttons button.selected {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: #fff;
  }

  .step_5 .idea_rating_grid {
    grid-template-columns: 1fr;
    gap: 0.3rem;
  }

  .step_5 .add_improvement_button {
    margin-left: auto;
  }

  .step_5 .add_improvement_button :global(.mainbutton) {
    height: 2.75rem !important;
    min-width: 0;
  }

  .step_5 .add_improvement_button.initial_improvement_button {
    width: 100%;
    margin: 0.9rem 0 0;
  }

  .step_5
    .add_improvement_button.initial_improvement_button
    :global(.mainbutton) {
    width: 100%;
  }

  .step_5 > .survey_footer {
    margin-top: 1rem;
    padding-top: 1.1rem;
  }

  @media (max-width: 1100px) {
    .step_5 .improvement_idea_content {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }
  }

  .survey_status.step_6 {
    width: min(54rem, 100%);
    padding: 2.5rem;
    border: 1px solid #e4e6e9;
    border-radius: 0.5rem;
  }

  .step_6 .review_summary {
    gap: 0.75rem;
    padding: 0 0 0.5rem;
  }

  .step_6 .review_card {
    padding: 1rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
  }

  .step_6 .review_card_header {
    justify-content: flex-start;
    align-items: center;
    gap: 0.45rem;
  }

  .step_6 .review_card_header h6 {
    margin: 0;
    font-size: 0.9rem;
  }

  .step_6 .review_card_header :global(button) {
    width: 1.65rem;
    height: 1.65rem;
    border-radius: 0.35rem;
  }

  .step_6 .review_card_header :global(button img) {
    width: 0.85rem;
    height: 0.85rem;
  }

  .step_6 .review_tags span {
    border-color: var(--pax-black);
    background: #fff;
    color: var(--pax-black);
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1;
  }

  .step_6 .review_task > strong {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.78rem;
  }

  .step_6 .review_task dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem 2rem;
  }

  .step_6 .review_task dl div {
    align-items: center;
    padding: 0.18rem 0;
    border: 0;
    border-radius: 0;
    font-size: 0.72rem;
  }

  .step_6 .review_detail_entry {
    margin-top: 0.65rem;
    padding-top: 0.65rem;
  }

  .step_6 .review_detail_metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
  }

  .step_6 .review_detail_metrics span {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.28rem 0.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 1rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1;
  }

  .step_6 .review_detail_metrics i {
    display: inline-block;
    width: 0.85rem;
    height: 0.85rem;
    background: #525a64;
    font-style: normal;
    position: relative;
    flex: 0 0 auto;
    mask: var(--metric-icon) center / contain no-repeat;
    -webkit-mask: var(--metric-icon) center / contain no-repeat;
  }

  .step_6 .review_detail_entry > p {
    margin: 0.65rem 0 0;
    color: #3f4650;
    font-size: 0.72rem;
  }

  .step_6 > .survey_footer {
    padding-top: 1rem;
  }

  .step_6 .step_footer {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .step_6 .step_footer > .buttons_wrapper {
    width: 100%;
  }

  .step_count {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-self: center;
    margin: 0;
    padding: 0 0.75rem;
    color: #59616c;
    font-size: 0.8rem !important;
    font-weight: 400;
    line-height: 1.2;
    white-space: nowrap;
  }

  :global(.step_6 .step_footer .buttons_wrapper button.button-primary) {
    height: 3rem !important;
    min-width: 9.5rem;
    background: #189447 !important;
    border-color: #189447 !important;
    color: var(--pax-white) !important;
  }

  @media (max-width: 900px) {
    .survey_status.welcome_step {
      display: flex;
      min-height: auto;
    }
    .welcome_content {
      padding: 0;
    }
    .welcome_step > .survey_footer {
      padding: 0;
    }
    .survey_status.step_1,
    .survey_status.step_2,
    .survey_status.step_3,
    .survey_status.step_4,
    .survey_status.step_5,
    .survey_status.step_6 {
      padding: 2.5rem;
    }
    .step_1 .survey_options > .buttons_wrapper,
    .step_2 .survey_options > .buttons_wrapper {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .step_3 .dimension_task {
      grid-template-columns: 8rem minmax(25rem, 1fr);
    }
  }
</style>
